import { defineBlock } from "eddev/blocks"
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import { useEvData } from "@hooks/queries"
import { Grid, Col } from "@components/layout/Grid"
import {
  ChartContainer,
  FloatingFilter,
  FuelTypeFilter,
  TickDate,
  Timeline,
  TimelineSelection,
} from "@components/atoms/data/Components"
import { ComponentPropsWithoutRef, forwardRef, Fragment, useEffect, useMemo, useRef, useState } from "react"
import { cn, tv } from "@utils/tw"
import { colors, extractQuarterlyData, groupedByFuelAndJurisdiction } from "@utils/evDataHelpers"
import { BEV, HFCEV, HYBRID, ICE, PHEV } from "@components/atoms/Icons"
import { AnimatePresence, useInView } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Button } from "@components/atoms/Button"

export const meta: BlockMeta = {
  title: "EV Sales Jurisdiction",
  category: "common",
  icon: "chart-bar",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

ChartJS.register(...registerables)

type VehicleData = {
  [quarter: string]: number
}

type State = "NSW" | "VIC" | "QLD" | "SA" | "WA" | "TAS" | "NT" | "ACT"

type Vehicles = {
  NSW: VehicleData[]
  VIC: VehicleData[]
  QLD: VehicleData[]
  SA: VehicleData[]
  WA: VehicleData[]
  TAS: VehicleData[]
  NT: VehicleData[]
  ACT: VehicleData[]
}

type Data = {
  "FUEL TYPE": string
  vehicles: Vehicles
}

type Totals = {
  [state: string]: number
}

type PercentageData = {
  current: { [fuelType: string]: number }
  previous: { [fuelType: string]: number }
}

export default defineBlock("data/evindex-sales-jurisdiction", (props) => {
  const evData = useEvData(undefined)
  const [currentPeriod, setCurrentPeriod] = useState<number | null>(null)
  const [currentFuelType, setCurrentFuelType] = useState<"ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV">("BEV")
  const chartRef = useRef<ChartJS>(null)
  const [chartData, setChartData] = useState<ChartData<"bar">>({
    datasets: [],
  })
  const isMobile = useIsMobile()

  const parsedData = useMemo(() => {
    if (evData.data?.evIndexData) {
      return groupedByFuelAndJurisdiction(evData.data.evIndexData)
    }
    return []
  }, [evData.data?.evIndexData])

  const quarters = useMemo(() => {
    if (parsedData.length > 0) {
      return extractQuarterlyData(parsedData[0].vehicles.ACT[0]).map((data) => data.quarter)
    }
    return []
  }, [parsedData])

  useEffect(() => {
    // Initial current quarter
    if (quarters) setCurrentPeriod(quarters.length - 1)
  }, [quarters])

  useEffect(() => {
    if (currentPeriod && parsedData.length > 0) {
      const calculateTotals = (data: Data, period: number): Totals =>
        Object.fromEntries(
          (["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"] as State[]).map((state) => [
            state,
            data.vehicles[state].reduce((a: number, b: VehicleData) => a + b[quarters[period]], 0),
          ]),
        )

      const calculatePercentage = (
        fuelType: string,
        state: string,
        totals: { fuelType: string; totals: Totals }[],
      ): number =>
        ((totals.find((type) => type.fuelType === fuelType)?.totals[state] || 0) /
          totals.reduce((a, b) => a + b.totals[state], 0)) *
        100

      const totals = parsedData.map((data) => ({
        fuelType: data["FUEL TYPE"],
        totalsPrev: calculateTotals(data, currentPeriod - 1),
        totals: calculateTotals(data, currentPeriod),
      }))

      totals.forEach((data) => {
        data.totalsPrev["Australia"] = Object.values(data.totalsPrev).reduce((a, b) => a + b, 0)
        data.totals["Australia"] = Object.values(data.totals).reduce((a, b) => a + b, 0)
      })

      const jurisdictions = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT", "Australia"]
      const fuelTypes = ["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"]

      const percentageByJurisdiction = Object.fromEntries(
        jurisdictions.map((state) => [
          state,
          {
            current: Object.fromEntries(
              fuelTypes.map((fuelType) => [fuelType, calculatePercentage(fuelType, state, totals)]),
            ),
            previous: Object.fromEntries(
              fuelTypes.map((fuelType) => [
                fuelType,
                calculatePercentage(
                  fuelType,
                  state,
                  totals.map((t) => ({
                    ...t,
                    totals: t.totalsPrev,
                  })),
                ),
              ]),
            ),
          },
        ]),
      )

      setChartData({
        labels: jurisdictions,
        datasets: [
          {
            label: quarters[currentPeriod - 1],
            data: jurisdictions.map((state) => percentageByJurisdiction[state].previous[currentFuelType]),
            backgroundColor: colors[`${currentFuelType}_50`],
            borderColor: colors[`${currentFuelType}_50`],
          },
          {
            label: quarters[currentPeriod],
            data: jurisdictions.map((state) => percentageByJurisdiction[state].current[currentFuelType]),
            backgroundColor: colors[currentFuelType],
            borderColor: colors[currentFuelType],
          },
        ],
      })
    }
  }, [currentPeriod, currentFuelType])

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: isMobile ? 1 : 3 / 1,

    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: `Penetration of ${currentFuelType}s in light new vehicle sales`,
          font: {
            weight: "bold",
          },
        },
        ticks: {
          callback: (value) => {
            return `${value}%`
          },
        },
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
        },
      },
    },

    animation: {
      duration: 500,
    },

    interaction: {
      mode: "index",
      intersect: false,
    },

    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 0,
        caretSize: 0,
        caretPadding: 20,
        padding: 16,
        boxPadding: 5,
        usePointStyle: true,

        callbacks: {
          labelColor: (ctx) => {
            return {
              borderColor: ctx.element.options.borderColor,
              backgroundColor: ctx.element.options.borderColor,
            }
          },
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
          label: (ctx) => {
            return `${ctx.dataset.label} : ${(ctx.dataset.data[ctx.dataIndex] as number).toFixed(2)} %`
          },
        },
      },

      legend: {
        display: false,
      },
    },
  }

  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: 0.5 })

  return (
    <ChartContainer ref={blockRef}>
      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
        {isMobile && (
          <AnimatePresence>
            {inView && (
              <FloatingFilter open={filterOpen} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Button
                  size="tiny"
                  color="yellow"
                  icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                  iconStyle="right"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  Filter this data
                </Button>

                <FuelTypeFilter>
                  <ul>
                    <li>
                      <button
                        className={`${currentFuelType === "ICE" ? `active` : ``} ice`}
                        onClick={() => setCurrentFuelType("ICE")}
                      >
                        <i>{ICE}</i>
                        <span>ICE</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                        onClick={() => setCurrentFuelType("BEV")}
                      >
                        <i>{BEV}</i>
                        <span>BEV</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType === "PHEV" ? `active` : ``} phev`}
                        onClick={() => setCurrentFuelType("PHEV")}
                      >
                        <i>{PHEV}</i>
                        <span>PHEV</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType === "Hybrid" ? `active` : ``} hybrid`}
                        onClick={() => setCurrentFuelType("Hybrid")}
                      >
                        <i>{HYBRID}</i>
                        <span>Hybrid</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                        onClick={() => setCurrentFuelType("HFCEV")}
                      >
                        <i>{HFCEV}</i>
                        <span>HFCEV</span>
                      </button>
                    </li>
                  </ul>
                </FuelTypeFilter>

                <TimelineSelection light>
                  {quarters.length > 0 && currentPeriod && (
                    <Timeline>
                      <ul>
                        {quarters.map((period, i) => {
                          if (i === 0) return <Fragment key={i}></Fragment>
                          return (
                            <li key={i}>
                              <TickDate
                                style={{
                                  opacity: i === currentPeriod ? 1 : i === 0 ? (currentPeriod === 1 ? 0 : 0.5) : 0,
                                }}
                              >
                                {period}
                              </TickDate>
                            </li>
                          )
                        })}
                      </ul>
                      <input
                        type="range"
                        min="1"
                        max={quarters.length - 1}
                        step={1}
                        defaultValue={quarters.length - 1}
                        list="period-marks"
                        onChange={(e) => setCurrentPeriod(parseInt(e.target.value))}
                      />
                    </Timeline>
                  )}{" "}
                </TimelineSelection>
              </FloatingFilter>
            )}
          </AnimatePresence>
        )}
        {!isMobile && (
          <Col initial={12}>
            <TimelineSelection light>
              <FuelTypeFilter>
                <ul>
                  <li>
                    <button
                      className={`${currentFuelType === "ICE" ? `active` : ``} ice`}
                      onClick={() => setCurrentFuelType("ICE")}
                    >
                      <i>{ICE}</i>
                      <span>ICE</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                      onClick={() => setCurrentFuelType("BEV")}
                    >
                      <i>{BEV}</i>
                      <span>BEV</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType === "PHEV" ? `active` : ``} phev`}
                      onClick={() => setCurrentFuelType("PHEV")}
                    >
                      <i>{PHEV}</i>
                      <span>PHEV</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType === "Hybrid" ? `active` : ``} hybrid`}
                      onClick={() => setCurrentFuelType("Hybrid")}
                    >
                      <i>{HYBRID}</i>
                      <span>Hybrid</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                      onClick={() => setCurrentFuelType("HFCEV")}
                    >
                      <i>{HFCEV}</i>
                      <span>HFCEV</span>
                    </button>
                  </li>
                </ul>
              </FuelTypeFilter>
              {quarters.length > 0 && currentPeriod && (
                <Timeline>
                  <ul>
                    {quarters.map((period, i) => {
                      if (i === 0) return <Fragment key={i}></Fragment>
                      return (
                        <li key={i}>
                          <TickDate
                            style={{ opacity: i === currentPeriod ? 1 : i === 0 ? (currentPeriod === 1 ? 0 : 0.5) : 0 }}
                          >
                            {period}
                          </TickDate>
                        </li>
                      )
                    })}
                  </ul>
                  <input
                    type="range"
                    min="1"
                    max={quarters.length - 1}
                    step={1}
                    defaultValue={quarters.length - 1}
                    list="period-marks"
                    onChange={(e) => setCurrentPeriod(parseInt(e.target.value))}
                  />
                </Timeline>
              )}
            </TimelineSelection>
          </Col>
        )}
        <Col initial={12}>
          <ChartInstance>
            <Chart type="bar" ref={chartRef} data={chartData} options={options} />
          </ChartInstance>
        </Col>
      </div>
    </ChartContainer>
  )
})

const chartInstanceStyles = tv({
  base: "w-full h-0 pt-[100%] relative [&_canvas]:absolute [&_canvas]:top-0 [&_canvas]:left-0 md:pt-[calc(100%*(1/3))]",
})

const ChartInstance = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(chartInstanceStyles(), className)} {...props} />
))
ChartInstance.displayName = "ChartInstance"
